/**
 * Input Sanitization Utilities
 *
 * Provides comprehensive sanitization for user inputs to prevent XSS and injection attacks.
 * Uses industry-standard libraries: DOMPurify and validator.js
 *
 * Business benefit: Protects application and users from malicious input while maintaining UX
 */

import DOMPurify from 'dompurify';
import validator from 'validator';

/**
 * Options for text sanitization
 */
export interface SanitizeOptions {
  readonly maxLength?: number;
  readonly trim?: boolean;
  readonly normalizeWhitespace?: boolean;
}

/**
 * Configuration for DOMPurify sanitization
 */
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span'],
  ALLOWED_ATTR: ['class', 'id'],
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false,
};

/**
 * Sanitize HTML content using DOMPurify
 * Removes potentially dangerous HTML/JavaScript while preserving safe formatting
 *
 * @param dirty - Untrusted HTML string
 * @param allowBasicFormatting - Allow basic text formatting tags (b, i, em, strong)
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```tsx
 * const userInput = '<script>alert("xss")</script><b>Hello</b>';
 * const safe = sanitizeHtml(userInput); // '<b>Hello</b>'
 * ```
 */
export function sanitizeHtml(
  dirty: string,
  allowBasicFormatting = false
): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  const config = allowBasicFormatting
    ? {
        ALLOWED_TAGS: [...PURIFY_CONFIG.ALLOWED_TAGS],
        ALLOWED_ATTR: [...PURIFY_CONFIG.ALLOWED_ATTR],
        KEEP_CONTENT: PURIFY_CONFIG.KEEP_CONTENT,
        RETURN_DOM: PURIFY_CONFIG.RETURN_DOM,
        RETURN_DOM_FRAGMENT: PURIFY_CONFIG.RETURN_DOM_FRAGMENT,
        RETURN_DOM_IMPORT: PURIFY_CONFIG.RETURN_DOM_IMPORT,
      }
    : {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [...PURIFY_CONFIG.ALLOWED_ATTR],
        KEEP_CONTENT: PURIFY_CONFIG.KEEP_CONTENT,
        RETURN_DOM: PURIFY_CONFIG.RETURN_DOM,
        RETURN_DOM_FRAGMENT: PURIFY_CONFIG.RETURN_DOM_FRAGMENT,
        RETURN_DOM_IMPORT: PURIFY_CONFIG.RETURN_DOM_IMPORT,
      };

  return DOMPurify.sanitize(dirty, config);
}

/**
 * Sanitize plain text input
 * Strips all HTML and normalizes whitespace
 *
 * @param input - Untrusted text input
 * @param options - Sanitization options
 * @returns Clean text string
 *
 * @example
 * ```tsx
 * const userInput = '<script>alert("xss")</script>  Hello   World  ';
 * const safe = sanitizeText(userInput); // 'Hello World'
 * ```
 */
export function sanitizeText(
  input: string,
  options: SanitizeOptions = {}
): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  const {
    maxLength = 10000,
    trim = true,
    normalizeWhitespace = true,
  } = options;

  // Strip all HTML tags
  let clean = validator.stripLow(input, true);
  clean = clean.replace(/<[^>]*>/g, '');

  // Normalize whitespace
  if (normalizeWhitespace) {
    clean = clean.replace(/\s+/g, ' ');
  }

  // Trim
  if (trim) {
    clean = clean.trim();
  }

  // Enforce max length
  if (clean.length > maxLength) {
    clean = clean.slice(0, maxLength);
  }

  return clean;
}

/**
 * Sanitize email address
 * Validates and normalizes email format
 *
 * @param email - Email address to sanitize
 * @returns Normalized email or empty string if invalid
 *
 * @example
 * ```tsx
 * sanitizeEmail('  User@Example.COM  '); // 'user@example.com'
 * sanitizeEmail('invalid-email'); // ''
 * ```
 */
export function sanitizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const trimmed = email.trim();

  if (!validator.isEmail(trimmed)) {
    return '';
  }

  return (
    validator.normalizeEmail(trimmed, {
      all_lowercase: true,
      gmail_remove_dots: false,
      gmail_remove_subaddress: false,
    }) || ''
  );
}

/**
 * Sanitize URL
 * Validates and normalizes URL, ensures safe protocols
 *
 * @param url - URL to sanitize
 * @param allowedProtocols - Allowed URL protocols
 * @returns Sanitized URL or empty string if invalid
 *
 * @example
 * ```tsx
 * sanitizeUrl('https://example.com'); // 'https://example.com'
 * sanitizeUrl('javascript:alert(1)'); // ''
 * ```
 */
export function sanitizeUrl(
  url: string,
  allowedProtocols: readonly string[] = ['http', 'https', 'mailto']
): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  const trimmed = url.trim();

  if (!validator.isURL(trimmed, { protocols: [...allowedProtocols] })) {
    return '';
  }

  // Extra security: ensure no javascript: protocol
  if (/javascript:/gi.test(trimmed)) {
    return '';
  }

  return trimmed;
}

/**
 * Sanitize a filename for DISPLAY and for storage in a text column.
 *
 * This is the name a family member READS — `event_attachments.file_name` is a
 * `text` column rendered straight into the attachment list — so it PRESERVES
 * UNICODE. A Bulgarian filename has to survive it intact: `снимка.jpg` comes
 * back as `снимка.jpg`. It is NOT the rule for a storage object key; that is
 * {@link sanitizeStorageObjectName}, which has to stay ASCII for the reasons
 * written out there. The two are deliberately different functions with
 * different outputs, and they live side by side here so the difference is
 * visible instead of being a silent divergence between two files.
 *
 * What it removes, and why each one:
 *   * everything up to the last path separator. A value arriving as
 *     `../../../etc/passwd` is a PATH, and the only part of it that is a
 *     filename is `passwd`;
 *   * any `..` pair left after that, so a value that is nothing but traversal
 *     — `..`, or the doubled `....//` form that survives a naive single strip
 *     of `../` — collapses to the empty string instead of to a parent
 *     reference;
 *   * `/` and `\`, which cannot survive the basename step but cost nothing to
 *     re-check if that step is ever changed;
 *   * `<>:"|?*`, the set Windows refuses in a filename and the set that makes
 *     a name awkward inside a URL;
 *   * control and format characters. U+202E RIGHT-TO-LEFT OVERRIDE is the one
 *     that matters: a name stored as `invoice`, U+202E, `gpj.exe` RENDERS in
 *     the attachment list as `invoiceexe.jpg` — a filename that lies about
 *     what it is. (The character is described rather than written here on
 *     purpose; pasting one into a source comment reverses the comment.)
 *
 * Whitespace runs collapse to `_`, and the result is capped at 255 CODE
 * POINTS rather than 255 UTF-16 units — cutting a string mid-surrogate leaves
 * a lone surrogate, which is not valid UTF-8 and which Postgres rejects on the
 * way into the column.
 *
 * WHY THE EXAMPLES BELOW ARE WORTH A TEST. The traversal step used to be
 * `.replaceAll(/../g, '')` with the dots UNESCAPED, so `..` meant "any two
 * characters" and the step deleted every adjacent pair in the string:
 * `photo.jpg` came back as `''`, and all three published examples were
 * unreachable. Nothing broke only because nothing called it — the single route
 * in is {@link sanitizeFormData}'s `'filename'` branch and no caller selects
 * that field type. `src/shared/lib/__tests__/sanitize.test.ts` pins the
 * examples so the doc and the code cannot drift apart again.
 *
 * @param filename - Filename to sanitize
 * @returns Safe filename, Unicode intact
 *
 * @example
 * ```tsx
 * sanitizeFilename('../../../etc/passwd'); // 'passwd'
 * sanitizeFilename('file<>?.txt'); // 'file.txt'
 * sanitizeFilename('my file name.pdf'); // 'my_file_name.pdf'
 * sanitizeFilename('снимка.jpg'); // 'снимка.jpg'
 * ```
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  // Basename first: the LAST non-empty segment of whatever separators the
  // value carries. Filtering empties before taking the last one is what makes
  // a trailing separator (`docs/`) yield `docs` rather than ''.
  const segments = filename
    .trim()
    .split(/[/\\]+/)
    .filter((segment) => segment !== '');
  const basename = segments.at(-1) ?? '';

  const cleaned = basename
    .replaceAll('..', '') // Remove path traversal
    .replaceAll(/[/\\]/g, '') // Remove path separators
    .replaceAll(/[<>:"|?*]/g, '') // Remove dangerous characters
    .replaceAll(/\s+/g, '_') // Replace whitespace runs with underscores
    .replaceAll(/[\p{Cc}\p{Cf}]/gu, ''); // Remove control / bidi-override chars

  // Cap by code point, not by UTF-16 unit — see the surrogate note above.
  return [...cleaned].slice(0, 255).join('');
}

/**
 * Bulgarian Cyrillic -> Latin, the official streamlined transliteration system
 * (Закон за транслитерацията / ISO-ish `а->a … щ->sht`).
 *
 * Used only by {@link sanitizeStorageObjectName}. The law's one contextual
 * rule — a word-final `-ия` transliterates as `-ia`, not `-iya` — is
 * deliberately NOT implemented: this produces a storage object key, not prose,
 * and a per-character map is the thing a reader can verify at a glance.
 */
const BULGARIAN_TRANSLITERATION: ReadonlyMap<string, string> = new Map(
  Object.entries({
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    е: 'e',
    ж: 'zh',
    з: 'z',
    и: 'i',
    й: 'y',
    к: 'k',
    л: 'l',
    м: 'm',
    н: 'n',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'ts',
    ч: 'ch',
    ш: 'sh',
    щ: 'sht',
    ъ: 'a',
    ь: 'y',
    ю: 'yu',
    я: 'ya',
    А: 'A',
    Б: 'B',
    В: 'V',
    Г: 'G',
    Д: 'D',
    Е: 'E',
    Ж: 'Zh',
    З: 'Z',
    И: 'I',
    Й: 'Y',
    К: 'K',
    Л: 'L',
    М: 'M',
    Н: 'N',
    О: 'O',
    П: 'P',
    Р: 'R',
    С: 'S',
    Т: 'T',
    У: 'U',
    Ф: 'F',
    Х: 'H',
    Ц: 'Ts',
    Ч: 'Ch',
    Ш: 'Sh',
    Щ: 'Sht',
    Ъ: 'A',
    Ь: 'Y',
    Ю: 'Yu',
    Я: 'Ya',
  })
);

/**
 * Sanitize a filename for use as a SUPABASE STORAGE OBJECT KEY segment.
 *
 * The other half of the pair with {@link sanitizeFilename}, and the one that
 * is actually on a write path: `storageService.buildObjectPath` calls it for
 * every upload, so its output is the tail of `<family_id>/<entity_id>/<uuid>-<name>`.
 * It used to live in `storageService.ts` as a private `sanitizeFileName`, an
 * undocumented second sanitiser with the house's single-sanitiser rule quietly
 * broken around it. It is here now, and the ASCII behaviour is unchanged.
 *
 * WHY THE KEY STAYS ASCII, when the display name above does not. A delete-path
 * invariant depends on it: `storageService.storageKeyForRemoval` REFUSES any
 * stored value containing '?' or '#' rather than cutting at the delimiter,
 * and it argues that this costs nothing on data this project wrote precisely
 * because this function emits only word characters, dot and dash. Widening the
 * key to Unicode would undercut a written-down safety rule for no gain — a key
 * is machine-facing, and the human-readable name is stored separately in the
 * database column that {@link sanitizeFilename} guards.
 *
 * WHY IT TRANSLITERATES. `\w` without the `u` flag is ASCII `[A-Za-z0-9_]`, so
 * before transliteration `снимка.jpg` lost every letter and produced the key
 * `<uuid>-.jpg`: the `|| 'file'` fallback never fired, because `'.jpg'` is not
 * empty. This family writes Bulgarian, so a Bulgarian name is the normal case,
 * not the exotic one. Transliterating first turns it into `snimka.jpg` and the
 * key stays readable in the Supabase dashboard. A name in a script the map
 * does not cover still reduces to its extension; that is acceptable because
 * the uuid prefix, not the name, is what keeps the key unique.
 *
 * @param name - Original file name
 * @returns Lowercase ASCII key segment, never empty, never containing '?' or '#'
 *
 * @example
 * ```tsx
 * sanitizeStorageObjectName('My Report.PDF'); // 'my-report.pdf'
 * sanitizeStorageObjectName('снимка.jpg'); // 'snimka.jpg'
 * sanitizeStorageObjectName('???'); // 'file'
 * ```
 */
export function sanitizeStorageObjectName(name: string): string {
  if (!name || typeof name !== 'string') {
    return 'file';
  }

  let transliterated = '';
  for (const character of name.trim()) {
    transliterated += BULGARIAN_TRANSLITERATION.get(character) ?? character;
  }

  // Keep word chars, dot and dash; collapse everything else.
  return (
    transliterated
      .replaceAll(/\s+/g, '-')
      .replaceAll(/[^\w.-]/g, '')
      .toLowerCase() || 'file'
  );
}

/**
 * Sanitize object with string values
 * Recursively sanitizes all string properties in an object
 *
 * @param obj - Object with potentially unsafe string values
 * @param sanitizer - Sanitization function to apply to each string
 * @returns New object with sanitized values
 *
 * @example
 * ```tsx
 * const userInput = {
 *   name: '  John Doe  ',
 *   bio: '<script>alert("xss")</script>',
 *   age: 25
 * };
 * const safe = sanitizeObject(userInput, sanitizeText);
 * // { name: 'John Doe', bio: '', age: 25 }
 * ```
 */
export function sanitizeObject<T extends Record<string, unknown>>(
  obj: T,
  sanitizer: (value: string) => string = sanitizeText
): T {
  const sanitized: Record<string, unknown> = {};

  for (const key in obj) {
    const value = obj[key];

    if (typeof value === 'string') {
      sanitized[key] = sanitizer(value);
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      sanitized[key] = sanitizeObject(
        value as Record<string, unknown>,
        sanitizer
      );
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item: unknown) => {
        if (typeof item === 'string') {
          return sanitizer(item);
        }
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          return sanitizeObject(item as Record<string, unknown>, sanitizer);
        }
        return item;
      });
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
}

/**
 * Sanitize form data before submission
 * Applies appropriate sanitization based on field types
 *
 * @param data - Form data object
 * @param fieldConfig - Configuration for each field
 * @returns Sanitized form data
 *
 * @example
 * ```tsx
 * const formData = {
 *   name: '  John  ',
 *   email: '  USER@EXAMPLE.COM  ',
 *   website: 'https://example.com',
 *   bio: '<script>alert("xss")</script><b>Hello</b>'
 * };
 *
 * const safe = sanitizeFormData(formData, {
 *   name: 'text',
 *   email: 'email',
 *   website: 'url',
 *   bio: 'html'
 * });
 * ```
 */
export function sanitizeFormData<T extends Record<string, unknown>>(
  data: T,
  fieldConfig?: Partial<
    Record<keyof T, 'text' | 'email' | 'url' | 'html' | 'filename'>
  >
): T {
  if (!fieldConfig) {
    return sanitizeObject(data);
  }

  const sanitized: Record<string, unknown> = { ...data };

  for (const key in data) {
    const value = data[key];
    const fieldType = fieldConfig[key];

    if (typeof value === 'string') {
      switch (fieldType) {
        case 'email':
          sanitized[key] = sanitizeEmail(value);
          break;
        case 'url':
          sanitized[key] = sanitizeUrl(value);
          break;
        case 'html':
          sanitized[key] = sanitizeHtml(value, true);
          break;
        case 'filename':
          sanitized[key] = sanitizeFilename(value);
          break;
        case 'text':
        default:
          sanitized[key] = sanitizeText(value);
          break;
      }
    }
  }

  return sanitized as T;
}

/**
 * Check if a string contains potential XSS
 * Useful for validation before sanitization
 *
 * @param input - String to check
 * @returns true if potential XSS detected
 */
export function containsXss(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const xssPatterns = [
    /<script[^>]*>.*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi, // Event handlers like onclick=
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
  ];

  return xssPatterns.some((pattern) => pattern.test(input));
}
